import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Badge } from "react-bootstrap";

/**
 * Local-only image manager for CREATE flow.
 * - Keeps selected files in local state (parent holds them).
 * - Parent will upload AFTER item create.
 *
 * Value format:
 * images: Array<{
 *   id: string,              // local id
 *   file: File,
 *   previewUrl: string
 * }>
 */
const ItemImagesModal = ({
  show,
  onHide,
  title = "Manage Images",
  images = [],
  onChangeImages, // (nextImages) => void
  selection = { primaryId: null, secondaryId: null },
  onChangeSelection, // (nextSelection) => void
  maxImages = 10,
}) => {
  const [localImages, setLocalImages] = useState(images);
  const [localSel, setLocalSel] = useState(selection);

  useEffect(() => {
    setLocalImages(images || []);
  }, [images]);

  useEffect(() => {
    setLocalSel(selection || { primaryId: null, secondaryId: null });
  }, [selection]);

  const canAddMore = useMemo(() => localImages.length < maxImages, [localImages, maxImages]);

  const ensurePrimarySecondaryDefaults = (imgs, sel) => {
    const next = { ...sel };

    // If primary missing but we have images -> set first as primary
    if (!next.primaryId && imgs.length > 0) next.primaryId = imgs[0].id;

    // If secondary missing and >=2 images -> set second as secondary (not same as primary)
    if (!next.secondaryId && imgs.length > 1) {
      const second = imgs.find((x) => x.id !== next.primaryId) || imgs[1];
      next.secondaryId = second?.id || null;
    }

    // If secondary == primary -> clear secondary
    if (next.secondaryId && next.secondaryId === next.primaryId) next.secondaryId = null;

    return next;
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const remaining = maxImages - localImages.length;
    const toTake = files.slice(0, Math.max(0, remaining));

    const mapped = toTake.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    const nextImages = [...localImages, ...mapped];
    const nextSel = ensurePrimarySecondaryDefaults(nextImages, localSel);

    setLocalImages(nextImages);
    setLocalSel(nextSel);
  };

  const removeImage = (id) => {
    const img = localImages.find((x) => x.id === id);
    if (img?.previewUrl) URL.revokeObjectURL(img.previewUrl);

    const nextImages = localImages.filter((x) => x.id !== id);

    let nextSel = { ...localSel };
    if (nextSel.primaryId === id) nextSel.primaryId = null;
    if (nextSel.secondaryId === id) nextSel.secondaryId = null;

    nextSel = ensurePrimarySecondaryDefaults(nextImages, nextSel);

    setLocalImages(nextImages);
    setLocalSel(nextSel);
  };

  const setPrimary = (id) => {
    let next = { ...localSel, primaryId: id };
    if (next.secondaryId === id) next.secondaryId = null;
    next = ensurePrimarySecondaryDefaults(localImages, next);
    setLocalSel(next);
  };

  const setSecondary = (id) => {
    if (id === localSel.primaryId) return;
    const next = ensurePrimarySecondaryDefaults(localImages, { ...localSel, secondaryId: id });
    setLocalSel(next);
  };

  const onSave = () => {
    // push back to parent
    onChangeImages?.(localImages);
    onChangeSelection?.(localSel);
    onHide?.();
  };

  const onClose = () => {
    // do not lose changes? you can decide:
    // Here: keep modal edits only when user clicks Save.
    // So just reset to props on close.
    setLocalImages(images || []);
    setLocalSel(selection || { primaryId: null, secondaryId: null });
    onHide?.();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <div className="small text-muted">
              Pick images, set Primary/Secondary. 
            </div>
          </div>
          <div className="small text-muted">
            {localImages.length}/{maxImages}
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Upload Images</Form.Label>
          <Form.Control
            type="file"
            multiple
            accept="image/*"
            disabled={!canAddMore}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = ""; // reset
            }}
          />
          {!canAddMore && (
            <div className="text-danger small mt-1">Max {maxImages} images reached.</div>
          )}
        </Form.Group>

        {localImages.length === 0 ? (
          <div className="text-muted">No images selected yet.</div>
        ) : (
          <div className="row g-2">
            {localImages.map((img) => {
              const isPrimary = localSel.primaryId === img.id;
              const isSecondary = localSel.secondaryId === img.id;

              return (
                <div className="col-6 col-md-4" key={img.id}>
                  <div className="border rounded p-2 h-100">
                    <div className="position-relative">
                      <img
                        src={img.previewUrl}
                        alt="preview"
                        style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 8 }}
                      />
                      <div className="position-absolute top-0 start-0 m-1 d-flex gap-1">
                        {isPrimary && <Badge bg="success">Primary</Badge>}
                        {isSecondary && <Badge bg="info">Secondary</Badge>}
                      </div>
                    </div>

                    <div className="d-flex gap-2 mt-2 flex-wrap">
                      <Button
                        size="sm"
                        variant={isPrimary ? "success" : "outline-success"}
                        onClick={() => setPrimary(img.id)}
                      >
                        Set Primary
                      </Button>
                      <Button
                        size="sm"
                        variant={isSecondary ? "info" : "outline-info"}
                        disabled={img.id === localSel.primaryId}
                        onClick={() => setSecondary(img.id)}
                      >
                        Set Secondary
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeImage(img.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSave}>
          Save Images
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ItemImagesModal;